/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {compareBy, Order, stableSort} from "../../helpers/TableHelpers";
import {FilterCombobox} from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import {JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels,} from "../../models/Jobs/JobRequestStatus";
import {ExportTemplatesToCsvRequest, JobRequest,} from "../../models/Jobs/JobRequest";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobsRepository from "../../repositories/JobsRepository";
import {UserRole} from "../../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import {cn} from "@/lib/utils";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Trash2, Download, XCircle} from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface ExportTemplatesToCsvStatusTableProps {
  organizationId: string;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const ExportTemplatesToCsvStatusTable: React.FC<ExportTemplatesToCsvStatusTableProps> = ({
  organizationId,
  userRole,
  isAssignedUser
}) => {
  const requiresReason = useRequiresReason();
  const [order, setOrder] = React.useState<Order>("desc");
  const [sort, setSort] = React.useState<keyof ExportTemplatesToCsvRequest>("exportName");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [exportTemplatesStatus, setExportTemplatesStatus] = useState<ExportTemplatesToCsvRequest[] | null>(null);
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  const templatesQuery = useQuery({ queryKey: [QUERY_KEYS.EXPORT_TEMPLATES, organizationId], queryFn:
    async () => {
      return await jobsRepository.getTemplatesCsvGenerationStatus(organizationId);
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (templatesQuery.data) {
      setExportTemplatesStatus(templatesQuery.data);
    }
  }, [templatesQuery]);

  const handleDownload = (url: string | undefined) => {
    if (url) { window.open(url, "_blank"); }
  };

  const changeSort = (newSort: keyof ExportTemplatesToCsvRequest) => { setSort(newSort); };
  const changeOrder = (newOrder: Order) => { setOrder(newOrder); };

  const { isError, isFetching } = templatesQuery;
  const loading = isFetching || isError;
  const filteredJobs = (exportTemplatesStatus || []).filter((job) => {
    return statusFilter == null || job.status === statusFilter;
  });

  const sortedJobs = sort
    ? stableSort<ExportTemplatesToCsvRequest>(filteredJobs, compareBy(order, sort))
    : filteredJobs;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerFilterItemClassName}>
          <Label className="text-sm">Status</Label>
          <FilterCombobox
            value={statusFilter}
            onChange={(newValue) => { setStatusFilter(newValue); }}
            placeholder="Status"
            options={Object.values(JobRequestStatus).filter((status) => status !== JobRequestStatus.New).map((status) => ({
              value: status,
              label: JobRequestStatusLabels[status as JobRequestStatus],
              color: JobRequestStatusColors[status as JobRequestStatus],
              icon: JobRequestStatusIcons[status as JobRequestStatus],
            }))}
          />
        </div>
      </div>

      <div className={cn(drawerTableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table className="table-fixed" containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[15%] min-w-[48px] text-center" />
              <TableHead className="w-[40%]">
                <SortableTableColumnHeader label={"Name"} sortProperty={"exportName"} getSort={() => sort} setSort={(newSort) => changeSort(newSort as keyof ExportTemplatesToCsvRequest)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} />
              </TableHead>
              <TableHead className="w-[30%]">
                <SortableTableColumnHeader label={"Status"} sortProperty={"status"} getSort={() => sort} setSort={(newSort) => changeSort(newSort as keyof JobRequest)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} />
              </TableHead>
              <TableHead className="w-[15%]" />
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedJobs.map((exportCsvJob) => {
              const canEdit = exportCsvJob.status === JobRequestStatus.Pending || exportCsvJob.status === JobRequestStatus.Completed;
              return (
                <TableRow key={exportCsvJob.id}>
                  <TableCell>
                    {requiresReason ? (
                      <ActionConfirmationModal actionConfig={ACTION_CONFIGS.EXPORT_TEMPLATES_LIBRARY} onConfirm={async (reason) => { try { await jobsRepository.auditLogTemplatesCsvExportDownload(exportCsvJob.id, reason); handleDownload(exportCsvJob.exportedCsvUrl); } catch (error) { throw error; } }}>
                        <Button variant="ghost" size="icon-sm" className="text-primary" aria-label="Download CSV" disabled={!exportCsvJob.exportedCsvUrl}><Download className="size-4" /></Button>
                      </ActionConfirmationModal>
                    ) : (
                      <Button variant="ghost" size="icon-sm" className="text-primary" aria-label="Download CSV" onClick={() => handleDownload(exportCsvJob.exportedCsvUrl)} disabled={!exportCsvJob.exportedCsvUrl}><Download className="size-4" /></Button>
                    )}
                  </TableCell>
                  <TableCell><span className="text-sm truncate block">{exportCsvJob.exportName}</span></TableCell>
                  <TableCell><JobRequestStatusChip status={exportCsvJob.status} /></TableCell>
                  {canEdit ? (
                    <TableCell>
                      <Button variant="ghost" size="icon-sm" className="text-destructive" aria-label="Cancel" onClick={() => { setAbortJob(exportCsvJob); }} disabled={!isAssignedUser && userRole !== UserRole.Admin}>
                        {exportCsvJob.status === JobRequestStatus.Completed ? (<Trash2 className="size-4" />) : (<XCircle className="size-4" />)}
                      </Button>
                    </TableCell>
                  ) : (<TableCell />)}
                </TableRow>
              );
            })}
              {sortedJobs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No exports.
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>
      {abortJob ? (
        <AbortJobModal open={abortJob !== null} jobRequest={abortJob} onClose={() => { setAbortJob(null); }}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_TEMPLATES, organizationId] });
            setAbortJob(null);
          }}
        />
      ) : (<></>)}
    </div>
  );
};

export default ExportTemplatesToCsvStatusTable;
